import {
  ElementProps,
  Text as RawText,
  TextProps as RawTextProps,
  useMatches,
} from '@mantine/core';
import colors, { ColorVariantType } from 'common/constants/colors.styles';
import typography, {
  TypographyVariantType,
} from 'common/constants/typography.styles';
import { PredefinedFirst } from 'types/helper.type';

// type RawTextProps = React.ComponentProps<typeof RawText<'p'>>;

export interface TextProps
  extends Omit<RawTextProps, 'variant'>,
    ElementProps<'p', keyof RawTextProps> {
  variant?: TypographyVariantType;
  color?: PredefinedFirst<ColorVariantType>;
  isResponsive?: boolean;
}

const sortedVariants = Object.entries(typography).sort(
  ([, a], [, b]) => b.fontSize - a.fontSize,
);

// responsive by closest size and keep fontWeight
function responsiveTextVariant(initialVariant: TypographyVariantType): {
  xl: React.CSSProperties;
  lg: React.CSSProperties;
  md: React.CSSProperties;
  sm: React.CSSProperties;
  xs: React.CSSProperties;
} {
  const currentTypography = typography[initialVariant];

  // Get index of initial variant
  const seenFontSizes = new Set();
  const uniqueTypographyArray = sortedVariants
    .filter(([key, style]) => {
      if (key === initialVariant) return true;
      if (style.fontWeight !== currentTypography.fontWeight) return false;
      if (seenFontSizes.has(style.fontSize)) {
        return false; // Skip if already seen
      }
      seenFontSizes.add(style.fontSize);
      return true;
    })
    .map(([key]) => key as TypographyVariantType);

  const index = uniqueTypographyArray.indexOf(initialVariant);

  // Ensure fontWeight remains the same for all responsive sizes
  const initialFontWeight = currentTypography?.fontWeight || 400;

  // Helper function to safely get a variant or use a fallback
  const getVariant = (idx: number, fallback: TypographyVariantType) => {
    return {
      ...(typography[uniqueTypographyArray[idx]] || typography[fallback]),
      fontWeight: initialFontWeight,
    } as React.CSSProperties;
  };

  const variant = {
    xl: getVariant(index, 'labelXSmall'),
    lg: getVariant(index + 1, 'labelXSmall'),
    md: getVariant(index + 1, 'labelXSmall'),
    sm: getVariant(index + 1, 'labelXSmall'),
    xs: getVariant(index + 2, 'labelXSmall'),
    base: getVariant(index + 2, 'labelXSmall'),
  };
  return variant;
}

export default function Text(props: TextProps) {
  const {
    variant = 'body3Regular',
    style,
    isResponsive = true,
    color = 'black',
    c,
    ...rest
  } = props;
  const textStyles = useMatches(responsiveTextVariant(variant));

  return (
    <RawText<'p'>
      c={typeof color === 'string' ? colors[color] || color : c}
      style={{
        ...(isResponsive ? textStyles : {}),
        ...style,
      }}
      {...rest}
    />
  );
}
